import React, {
  lazy,
  Suspense,
  startTransition,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { lazyLoadGlobalStyles } from "./styleLoader";

import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter, useLocation } from "react-router-dom";
import Context from "./context/Context";
import RecaptchaLoadContext from "./recaptchaContext";
import { loadC2aiOnInteraction, loadGtmOnInteraction } from "./setupScripts";

const GoogleReCaptchaProvider = lazy(() =>
  import("react-google-recaptcha-v3").then((module) => ({
    default: module.GoogleReCaptchaProvider,
  })),
);

const normalizePathname = (pathname) => {
  if (typeof pathname !== "string" || pathname.length === 0) {
    return "/";
  }
  if (pathname === "/") {
    return "/";
  }
  return pathname.replace(/\/+$/, "");
};

const isC2aiAllowedPath = (pathname) => {
  const normalizedPathname = normalizePathname(pathname);

  if (normalizedPathname === "/") {
    return true;
  }
  if (
    normalizedPathname === "/shop" ||
    normalizedPathname.startsWith("/shop/")
  ) {
    return true;
  }
  if (
    normalizedPathname === "/bonus" ||
    normalizedPathname.startsWith("/bonus/")
  ) {
    return true;
  }
  if (
    normalizedPathname === "/product" ||
    normalizedPathname.startsWith("/product/")
  ) {
    return true;
  }
  return (
    normalizedPathname === "/my-account-info" ||
    normalizedPathname === "/create-membership" ||
    normalizedPathname === "/order-step-1" ||
    normalizedPathname === "/membership-checkout" ||
    normalizedPathname === "/dashboard" ||
    normalizedPathname === "/cart" ||
    normalizedPathname === "/contact-us" ||
    normalizedPathname === "/checkout-2" ||
    normalizedPathname === "/contact-us" ||
    normalizedPathname === "/discount" ||
    normalizedPathname === "/confirmation" ||
    normalizedPathname === "/referral"
  );
};

const loaderFallback = (
  <div className="loader_new">
    <div className="three-body">
      <div className="three-body__dot"></div>
      <div className="three-body__dot"></div>
      <div className="three-body__dot"></div>
    </div>
  </div>
);

const root = ReactDOM.createRoot(document.getElementById("root"));
const RootApp = () => {
  const location = useLocation();
  const [recaptchaKey, setRecaptchaKey] = useState(null);
  const recaptchaRequestedRef = useRef(false);
  const c2aiLoadedRef = useRef(false);
  const gtmLoadedRef = useRef(false);
  const isAdminPath = location.pathname.startsWith("/admin");
  const shouldLoadRecaptcha = !isAdminPath;
  const shouldLoadC2ai = !isAdminPath && isC2aiAllowedPath(location.pathname);
  const shouldLoadGtm = !isAdminPath;
  const requestRecaptchaLoad = useCallback(() => {
    if (recaptchaRequestedRef.current || !shouldLoadRecaptcha) {
      return;
    }
    recaptchaRequestedRef.current = true;
    startTransition(() => {
      setRecaptchaKey("6Legt0UfAAAAAK0pJuaswZ4vni2Y5E1l19_EK3wZ");
    });
  }, [shouldLoadRecaptcha]);

  useEffect(() => {
    lazyLoadGlobalStyles();

    const shouldAttachListeners =
      (shouldLoadRecaptcha && !recaptchaRequestedRef.current) ||
      (shouldLoadC2ai && !c2aiLoadedRef.current) ||
      (shouldLoadGtm && !gtmLoadedRef.current);

    if (!shouldAttachListeners) {
      return undefined;
    }

    const interactiveEvents = [
      "scroll",
      "touchstart",
      "wheel",
      "click",
      "keydown",
    ];
    const options = { passive: true };

    const triggerThirdParties = () => {
      if (shouldLoadRecaptcha) {
        requestRecaptchaLoad();
      }
      if (shouldLoadC2ai) {
        if (!c2aiLoadedRef.current) {
          c2aiLoadedRef.current = true;
          loadC2aiOnInteraction();
        }
      }
      if (shouldLoadGtm) {
        if (!gtmLoadedRef.current) {
          gtmLoadedRef.current = true;
          loadGtmOnInteraction();
        }
      }

      const shouldDetachListeners =
        (!shouldLoadRecaptcha || recaptchaRequestedRef.current) &&
        (!shouldLoadC2ai || c2aiLoadedRef.current) &&
        (!shouldLoadGtm || gtmLoadedRef.current);

      if (shouldDetachListeners) {
        interactiveEvents.forEach((event) =>
          window.removeEventListener(event, triggerThirdParties, options),
        );
      }
    };

    interactiveEvents.forEach((event) =>
      window.addEventListener(event, triggerThirdParties, options),
    );

    return () => {
      interactiveEvents.forEach((event) =>
        window.removeEventListener(event, triggerThirdParties, options),
      );
    };
  }, [
    location.pathname,
    requestRecaptchaLoad,
    shouldLoadRecaptcha,
    shouldLoadC2ai,
    shouldLoadGtm,
  ]);

  return (
    <RecaptchaLoadContext.Provider value={requestRecaptchaLoad}>
      <Suspense fallback={loaderFallback}>
        <GoogleReCaptchaProvider
          reCaptchaKey={recaptchaKey ?? undefined}
          scriptProps={{
            async: true,
            defer: true,
            appendTo: "body",
          }}
        >
          <App />
        </GoogleReCaptchaProvider>
      </Suspense>
    </RecaptchaLoadContext.Provider>
  );
};

root.render(
  <React.StrictMode>
    <Context>
      <BrowserRouter>
        <RootApp />
      </BrowserRouter>
    </Context>
  </React.StrictMode>,
);

reportWebVitals();
